import { AlertCircle, Mic, MicOff, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';

interface MicrophonePermissionProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  language: 'en' | 'ar';
}

type PermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

export function MicrophonePermission({
  onPermissionGranted,
  onPermissionDenied,
  language,
}: MicrophonePermissionProps) {
  const [state, setState] = useState<PermissionState>('prompt');

  const checkPermission = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setState('unsupported');
        return;
      }

      // Check if permissions API is available
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        const updateState = () => {
          if (result.state === 'granted') {
            setState('granted');
            onPermissionGranted?.();
          } else if (result.state === 'denied') {
            setState('denied');
            onPermissionDenied?.();
          } else {
            setState('prompt');
          }
        };

        result.onchange = updateState;
        updateState();
      } else {
        // Fallback for browsers without permissions.query
        setState('prompt');
      }
    } catch (err) {
      console.error('[MicrophonePermission] Error checking permission:', err);
      setState('prompt');
    }
  }, [onPermissionGranted, onPermissionDenied]);

  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Successfully got stream, permission granted
      stream.getTracks().forEach(track => track.stop());
      setState('granted');
      onPermissionGranted?.();
    } catch (err: any) {
      console.warn('[MicrophonePermission] Permission request failed:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setState('denied');
        onPermissionDenied?.();
      }
    }
  };

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  if (state === 'granted') return null;

  const content = {
    en: {
      promptTitle: 'Microphone Access Required',
      promptDesc: 'To use voice ordering, we need access to your microphone.',
      allowBtn: 'Allow Microphone',
      deniedTitle: 'Microphone Blocked',
      deniedDesc: 'It looks like microphone access is blocked. Please enable it in your browser settings to use voice ordering.',
      settingsHint: 'Click the camera icon or site settings in your address bar to unblock.',
      unsupportedTitle: 'Not Supported',
      unsupportedDesc: 'Your browser does not support voice features. Please try a modern browser like Chrome or Safari.',
    },
    ar: {
      promptTitle: 'مطلوب الوصول إلى الميكروفون',
      promptDesc: 'لاستخدام الطلب الصوتي، نحتاج إلى الوصول إلى الميكروفون الخاص بك.',
      allowBtn: 'السماح للميكروفون',
      deniedTitle: 'الميكروفون محظور',
      deniedDesc: 'يبدو أن الوصول إلى الميكروفون محظور. يرجى تفعيله من إعدادات المتصفح لاستخدام الطلب الصوتي.',
      settingsHint: 'انقر على أيقونة الكاميرا أو إعدادات الموقع في شريط العناوين لإلغاء الحظر.',
      unsupportedTitle: 'غير مدعوم',
      unsupportedDesc: 'متصفحك لا يدعم الميزات الصوتية. يرجى تجربة متصفح حديث مثل كروم أو سفاري.',
    },
  }[language];

  return (
    <div className="p-4 w-full max-w-md mx-auto">
      {state === 'prompt' && (
        <Alert>
          <Mic className="h-4 w-4" />
          <AlertTitle>{content.promptTitle}</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 mt-2">
            <p>{content.promptDesc}</p>
            <Button onClick={requestPermission} className="bg-accent-gold hover:bg-accent-gold/90 text-white w-full">
              {content.allowBtn}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {state === 'denied' && (
        <Alert variant="destructive" style={{ background: 'var(--alert-destructive-bg)', borderColor: 'var(--error-light)' }}>
          <MicOff className="h-4 w-4" style={{ color: 'var(--error)' }} />
          <AlertTitle style={{ color: 'var(--error)' }}>{content.deniedTitle}</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 mt-2">
            <p style={{ color: 'var(--text-secondary)' }}>{content.deniedDesc}</p>
            <div className="flex items-center gap-2 text-xs opacity-80" style={{ color: 'var(--text-secondary)' }}>
              <Settings className="h-3 w-3" />
              <span>{content.settingsHint}</span>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {state === 'unsupported' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{content.unsupportedTitle}</AlertTitle>
          <AlertDescription>
            {content.unsupportedDesc}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

